import {
  Button,
  Stack,
  OutlinedInput,
  Box,
  CircularProgress,
  ToggleButtonGroup,
  Chip,
  ToggleButton,
  Skeleton,
} from "@mui/material";
import { useState, useCallback, useMemo, forwardRef, useEffect } from "react";
import Modal, { CommonModalProps } from "..";
import SearchIcon from "@mui/icons-material/Search";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import ItemCollectionCard from "../../Cards/ItemCollectionCard/item-collection-card";
import EmptyListSlot from "../../Misc/EmptyListSlot";
import { assetService, searchService } from "../../../Core/Api/services";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { debounce } from "throttle-debounce";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { SearchResponse } from "../../../Core/Api/Types/search-response";
import { SearchCardSkeleton } from "../../Cards/SearchCard/SearchCard";

type CloseClbProps = {
  message: string;
};

export type ModalAddItemsToCollectionElementProps = {
  collectionId: number;
  currentIds: number[];
  onClose?: (props: CloseClbProps) => void;
  addItem: (asset: DbAssetObjectInfos) => void;
  removeItem: (asset: DbAssetObjectInfos) => void;
};

export type ModalAddItemsToCollectionProps = CommonModalProps<ModalAddItemsToCollectionElementProps>;

const ModalAddItemsToCollection = forwardRef<any, ModalAddItemsToCollectionProps>((props, ref) => {
  const enqueueCustomSnackbar = useCustomSnackbar();
  const { collectionId, closeModal, onClose } = props;
  const [searchValue, setSearchValue] = useState<string>("");
  const [forceUpdate, setForceUpdate] = useState<boolean>(true);
  const [layerFilter, setLayerFilter] = useState<ObjectLayer | "">("");

  const { t } = useTranslation();

  const handleDoneClick = () => {
    onClose && onClose({ message: t("modal:message.addToCollectionClosed") });
    closeModal();
  };

  const [searchResults, setSearchResults] = useState<(DbAssetObjectInfos & { searchStatus: "added" | "none" })[]>([]);
  const [collectionCurrentIds, setCollectionCurrentIds] = useState<number[]>(props.currentIds);
  const [searchLoading, setSearchLoading] = useState<boolean>(true);

  const onClickAdd = useCallback(
    (id: number) => {
      assetService.AddObjectIdsToCollection(collectionId, [id]).then((data) => {
        if (!data.success) {
          enqueueCustomSnackbar(data.message!, "error");
          return;
        }

        let asset = searchResults.find((x) => x.id === id);
        if (asset !== undefined) {
          props.addItem(asset);
          let list = collectionCurrentIds.concat(asset.id);
          setCollectionCurrentIds(list);
        }
        setSearchResults((prevState) =>
          prevState.map((asset) => ({
            ...asset,
            searchStatus: asset.id === id ? "added" : asset.searchStatus,
          }))
        );
        setForceUpdate(!forceUpdate);
      });
    },
    [searchResults, forceUpdate] // trick
  );

  const onClickRemove = useCallback(
    (id: number) => {
      assetService.DeleteFromCollection(collectionId, [id]).then((data) => {
        if (!data.success) return;

        let asset = searchResults.find((x) => x.id === id);
        if (asset !== undefined) {
          let list = collectionCurrentIds.filter((x) => x !== asset.id);
          props.removeItem(asset);
          setCollectionCurrentIds(list);
        }

        setSearchResults((prevState) =>
          prevState.map((asset) => ({
            ...asset,
            searchStatus: asset.id === id ? "none" : asset.searchStatus,
          }))
        );
      });
      setForceUpdate(!forceUpdate);
    },
    [searchResults, forceUpdate] // trick
  );

  const searchCallback = (searchValue, layerFilter) => {
    setSearchResults([]);
    setSearchLoading(true);

    const searchPromises: Promise<SearchResponse>[] = [];

    if (layerFilter === ObjectLayer.Title || layerFilter === "") {
      searchPromises.push(
        searchService.SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Title,
            hideTitleWithoutAsset: true,
            limit: layerFilter === "" ? 25 : 100,
          })
        )
      );
    }

    if (layerFilter === ObjectLayer.Asset || layerFilter === "") {
      searchPromises.push(
        searchService.SearchBasic(
          new SearchRequestNative({
            pattern: searchValue,
            outputLayer: ObjectLayer.Asset,
            hideTitleWithoutAsset: true,
            limit: layerFilter === "" ? 25 : 100,
          })
        )
      );
    }

    if (layerFilter === ObjectLayer.Clip || layerFilter === "") {
      searchService.SearchBasic(
        new SearchRequestNative({
          pattern: searchValue,
          outputLayer: ObjectLayer.Clip,
          hideTitleWithoutAsset: true,
          limit: layerFilter === "" ? 25 : 100,
        })
      );
    }

    Promise.all(searchPromises)
      .then((responses) => {
        type SearchResult = DbAssetObjectInfos & { searchStatus: "added" | "none" };
        let mappedSearchResults: SearchResult[] = [];

        for (const response of responses) {
          if (!response.success) {
            enqueueCustomSnackbar(t("alert:error.searchingForItems", { message: response.message }), "error");
            setSearchResults([]);
            setSearchLoading(false);
            return;
          }

          const responseResults = (response.results ?? []).map((result) => ({
            ...result,
            searchStatus: (collectionCurrentIds.includes(result.id) ? "added" : "none") as "added" | "none",
          }));

          mappedSearchResults.push(...responseResults);
        }

        setSearchResults(mappedSearchResults);
      })
      .finally(() => setSearchLoading(false));
  };

  const searchCallbackDebounced = useMemo(() => debounce(1000, searchCallback, { atBegin: false }), [searchCallback]);

  useEffect(() => {
    if (searchValue !== "" && searchValue.length > 2) {
      searchCallbackDebounced(searchValue, layerFilter);
    }
  }, [searchValue, layerFilter]);

  const searchResultsCards = useMemo(() => {
    return searchResults.map((asset) => (
      <ItemCollectionCard
        asset={asset}
        onClickAdd={() => onClickAdd(asset.id)}
        onClickRemove={() => onClickRemove(asset.id)}
        status={asset.searchStatus}
        key={asset.id}
      />
    ));
  }, [searchResults, forceUpdate]);

  return (
    <Modal
      title={t("modal:modalAddToCollection:title")}
      reference={ref}
      closeModal={closeModal}
      width={504}
      height={"80vh"}
      footer={
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button onClick={closeModal}>{t("common:close")}</Button>
          <Button onClick={handleDoneClick} variant="contained" color="primary">
            {t("common:done")}
          </Button>
        </Stack>
      }
    >
      <Stack mb={2} gap={1}>
        <ToggleButtonGroup
          size="small"
          value={layerFilter}
          exclusive
          onChange={(e, value) => setLayerFilter(value as ObjectLayer)}
        >
          <ToggleButton fullWidth value="">
            {t("common:all")}
          </ToggleButton>
          <ToggleButton fullWidth value={ObjectLayer.Title}>
            {t("common:titles")}
          </ToggleButton>
          <ToggleButton fullWidth value={ObjectLayer.Asset}>
            {t("common:assets")}
          </ToggleButton>
          <ToggleButton fullWidth value={ObjectLayer.Clip}>
            {t("common:clips")}
          </ToggleButton>
        </ToggleButtonGroup>
        <OutlinedInput
          label={t("common:standard")}
          placeholder={t("common:search")}
          endAdornment={
            searchLoading ? (
              <Stack justifyContent="center" alignItems="center">
                <CircularProgress size={20} />
              </Stack>
            ) : (
              <SearchIcon />
            )
          }
          onChange={(e) => setSearchValue(e.target.value)}
          value={searchValue}
        />
      </Stack>
      <Box sx={(theme) => ({ ...theme.helpers(theme).scrollableContainer })}>
        {searchLoading ? (
          <Stack gap={1}>
            {[...Array(8)].map((_, index) => (
              <SearchCardSkeleton key={index} />
            ))}
          </Stack>
        ) : (
          <>
            {searchValue?.length > 0 ? (
              <Stack spacing={1}>
                <>
                  {searchResults.length > 0 ? (
                    <>{searchResultsCards}</>
                  ) : (
                    <EmptyListSlot
                      title={t("component:emptyListSlot.title.noSearchResults")}
                      desc={t("component:emptyListSlot.desc.noSearchResults")}
                      icon={<CollectionsBookmarkOutlinedIcon />}
                      sx={{ background: "transparent" }}
                      height="100%"
                    ></EmptyListSlot>
                  )}
                </>
              </Stack>
            ) : (
              <EmptyListSlot
                title={t("component:emptyListSlot.title.itIsEmptyHere")}
                desc={t("component:emptyListSlot.desc.searchForContent")}
                icon={<CollectionsBookmarkOutlinedIcon />}
                sx={{ background: "transparent" }}
                height="100%"
              ></EmptyListSlot>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
});

export default ModalAddItemsToCollection;
